import { getWidgetIdFromWidgetName, getWidgetNameFromWidgetId, type Widget } from '@wix/members-area-app-definitions';

import type { RouteConfiguration } from '../../../../types/controllers';

export interface CompressedRoutes {
  version: 1;
  paths: string[];
  states: string[];
  vfrValues: string[];
  data: CompressedRoute[];
}

type ValidRoute = RouteConfiguration & {
  state: string;
};

interface CompressedRoute {
  p: number; // path index
  s: number; // state index
  w: Widget; // Widget enum string
  h?: 1; // home (only if true)
  r?: 0 | 1; // private (0 for false, 1 for true)
  v?: number[]; // vfr indices (only if exists)
}

const extractUniqueRouteFields = (
  routes: RouteConfiguration[],
): {
  paths: string[];
  states: string[];
  vfrValues: string[];
} => {
  const paths = new Set<string>();
  const states = new Set<string>();
  const vfrValues = new Set<string>();

  routes.forEach((route) => {
    if (route.path) {
      paths.add(route.path);
    }
    if (route.state) {
      states.add(route.state);
    }
    if (route.vfr) {
      route.vfr.forEach((role) => vfrValues.add(role));
    }
  });

  return {
    paths: Array.from(paths),
    states: Array.from(states),
    vfrValues: Array.from(vfrValues),
  };
};

const isValidRoute = (route: RouteConfiguration): route is ValidRoute => {
  return !!(route.path && route.state && route.widgetId && getWidgetNameFromWidgetId(route.widgetId) !== undefined);
};

export const encodeRoutes = (
  routes: RouteConfiguration[],
  options?: { log?: (message: string, data?: any) => void },
): CompressedRoutes => {
  const validRoutes = routes.filter(isValidRoute);

  if (routes.length !== validRoutes.length && options?.log) {
    options.log('Routes compression: some routes were filtered out', {
      routes: JSON.stringify(routes),
    });
  }

  const { paths, states, vfrValues } = extractUniqueRouteFields(routes);

  const data = validRoutes.map((route) => {
    const compressed: CompressedRoute = {
      p: paths.indexOf(route.path),
      s: states.indexOf(route.state),
      w: getWidgetNameFromWidgetId(route.widgetId)!,
    };

    if (route.home) {
      compressed.h = 1;
    }
    if (route.private === true) {
      compressed.r = 1;
    }
    if (route.private === false) {
      compressed.r = 0;
    }
    if (route.vfr) {
      compressed.v = route.vfr.map((role) => vfrValues.indexOf(role));
    }

    return compressed;
  });

  return { version: 1 as const, paths, states, vfrValues, data };
};

export const decodeRoutes = (compressed: CompressedRoutes): RouteConfiguration[] => {
  const { paths, states, vfrValues, data } = compressed;

  return data.map((item) => {
    const route: RouteConfiguration = {
      path: paths[item.p],
      state: states[item.s],
      widgetId: getWidgetIdFromWidgetName(item.w),
    };

    if (item.h) {
      route.home = true;
    }
    if (item.r === 1) {
      route.private = true;
    }
    if (item.r === 0) {
      route.private = false;
    }
    if (item.v) {
      route.vfr = item.v.map((idx) => vfrValues[idx]);
    }

    return route;
  });
};

export const isCompressedData = (data: any): data is CompressedRoutes => {
  return !!data && !Array.isArray(data) && typeof data === 'object' && data.version === 1;
};
